/* eslint-disable consistent-return */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button, Input, Modal, notification, theme, Tooltip } from 'antd';
import { UserDeleteOutlined } from '@ant-design/icons';
import { useDebounce } from 'util/Hooks';
import { actions } from 'redux/paquers/slice';
import { useUnassignShipmentDriver } from 'services/journeys';
import usePaquers from 'redux/paquers';

const WAIT_QR_SCAN = 600;

const UnassignPaquerButton = ({ packet, size, onFinish }) => {
  usePaquers();
  const dispatch = useDispatch();
  const [unassignExternalCode, setUnassignExternalCode] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const debouncedUnassignExternalCode = useDebounce(
    unassignExternalCode,
    WAIT_QR_SCAN,
  );
  const { token } = theme.useToken();
  const { mutateAsync: unassignShipment } = useUnassignShipmentDriver();

  useEffect(() => {
    const unassingPacket = async (id) => {
      try {
        await unassignShipment({ shipmentId: parseInt(id, 10) });
        dispatch(actions.removePacket(id));
      } catch {
        // el hook ya notifica el error (incluye "Ver ruta" si está en una ruta iniciada)
      } finally {
        setModalVisible(false);
        if (onFinish) onFinish();
      }
    };

    setUnassignExternalCode(null);
    if (!debouncedUnassignExternalCode || debouncedUnassignExternalCode === '')
      return;

    try {
      const objectCode = JSON.parse(debouncedUnassignExternalCode);
      if (objectCode.id) return setUnassignExternalCode(() => objectCode.id);
    } catch (e) {
      // do nothing
    }

    // ponytail: los bultos ya no se controlan escaneándolos uno por uno,
    // alcanza el código externo del paquete
    if (debouncedUnassignExternalCode !== packet.externalCode)
      return notification.error({
        message: 'Ocurrio un problema',
        description: `El código externo no corresponde al paquete a liberar.`,
      });

    unassingPacket(packet.id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUnassignExternalCode]);

  return (
    <>
      <Modal
        closable
        destroyOnClose
        maskClosable
        open={modalVisible}
        title={
          <div style={{ display: 'flex', gap: '3%' }}>
            <UserDeleteOutlined style={{ paddingTop: '1%' }} />
            <p style={{ marginBottom: 0 }}>
              ¿Estas seguro que deseas liberar al paquer?
            </p>
          </div>
        }
        footer={null}
        onCancel={() => setModalVisible(false)}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            padding: '0 1% 1% 1%',
          }}
        >
          <p>
            Para liberar al paquer del paquete {packet.externalCode} es
            necesario <b>escanear el código de barras</b>.
          </p>
          <Input
            value={unassignExternalCode}
            autoFocus
            style={{ textAlign: 'center' }}
            onChange={(e) => {
              setUnassignExternalCode(e.target.value);
            }}
          />
        </div>
      </Modal>
      {size === 'small' ? (
        <Tooltip placement="top" title="Liberar paquer">
          <UserDeleteOutlined
            style={{
              color: token.colorPrimary,
            }}
            onClick={() => setModalVisible(true)}
          />
        </Tooltip>
      ) : (
        <Button onClick={() => setModalVisible(true)} type="primary" danger>
          Liberar paquer
        </Button>
      )}
    </>
  );
};

UnassignPaquerButton.propTypes = {
  packet: PropTypes.shape({
    externalCode: PropTypes.string,
    id: PropTypes.number,
  }),
  size: PropTypes.string,
  onFinish: PropTypes.func,
};

UnassignPaquerButton.defaultProps = {
  packet: {},
  size: 'large',
  onFinish: null,
};

export default UnassignPaquerButton;
